import { useMemo } from 'react';
import { colors } from '@/lib/ui-tokens';
import { encodeQr } from '../lib/qr-encoder';

interface QrCodeProps {
  value: string;
  /** Rendered edge length in CSS pixels. */
  size?: number;
  /** Quiet zone in modules; the spec asks for four. */
  quietZone?: number;
  title?: string;
  className?: string;
}

/**
 * Draws the module matrix as one SVG path — vector, so it prints at whatever
 * resolution the printer has, and no canvas or image round-trip is involved.
 */
export function QrCode({ value, size = 160, quietZone = 4, title, className }: QrCodeProps) {
  const matrix = useMemo(() => {
    try {
      return encodeQr(value);
    } catch {
      return null;
    }
  }, [value]);

  const path = useMemo(() => {
    if (!matrix) return '';
    const parts: string[] = [];
    for (let row = 0; row < matrix.size; row += 1) {
      let run = 0;
      for (let col = 0; col <= matrix.size; col += 1) {
        const dark = col < matrix.size && matrix.modules[row][col];
        if (dark) {
          run += 1;
          continue;
        }
        if (run > 0) {
          parts.push(`M${col - run + quietZone} ${row + quietZone}h${run}v1h-${run}z`);
          run = 0;
        }
      }
    }
    return parts.join('');
  }, [matrix, quietZone]);

  if (!matrix) return null;

  const total = matrix.size + quietZone * 2;

  return (
    <svg
      viewBox={`0 0 ${total} ${total}`}
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title ?? value}
      shapeRendering="crispEdges"
    >
      <path d={path} fill={colors.neutral[900]} />
    </svg>
  );
}
